import { cn } from '@commenda/utils';
import { ChevronDown, PlusIcon, XIcon } from 'lucide-react';
import * as React from 'react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../internal/Command';
import { Badge } from '../Badge';
import { Checkbox } from '../Checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { Separator } from '../Separator';
import { Tooltip, type TooltipProps } from '../Tooltip';

interface Option {
  label: string;
  value: string;
  disabled?: boolean;
  /**
   * Optional group label. When any option has a `group`, options are rendered
   * under group headings (grouped by first appearance order of the label).
   * Options without a `group` are rendered in an ungrouped section.
   */
  group?: string;
}

// Sentinel key for options that don't belong to a labelled group.
const UNGROUPED_KEY = '__ungrouped__';

interface MultiSelectProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  options: Option[];
  onValueChange: (value: string[]) => void;
  defaultValue: string[];
  placeholder?: string;
  maxCount?: number;
  modalPopover?: boolean;
  asChild?: boolean;
  _className?: string;
  renderOption?: (props: {
    option: Option;
    isSelected: boolean;
    onSelect: (value: string) => void;
  }) => React.ReactNode;
  renderTrigger?: (props: {
    selectedOptions: Option[];
    placeholder: string;
  }) => React.ReactNode;
  searchText?: string;
  notFoundText?: string;
  selectAllText?: string;
  error?: string;
  disabled?: boolean;
  addItemConfig?: {
    shouldAllowAdd: boolean;
    onAddItem: () => void;
    placeholder?: string;
  };
  hideSelectAll?: boolean;
  hideTags?: boolean;
  id?: string;
  hideSearch?: boolean;
  maxSelectable?: number | null;
  dropdownAlignment?: 'start' | 'center' | 'end';
  hideClearAll?: boolean;
  listClassName?: string;
  /** Wrap the trigger in a Tooltip. Same props as <Tooltip> (minus children/asChild). */
  triggerTooltip?: Omit<TooltipProps, 'children' | 'asChild'>;
}

const MultiSelect = React.forwardRef<HTMLButtonElement, MultiSelectProps>(
  (
    {
      options,
      onValueChange,
      defaultValue = [],
      placeholder = 'Select options',
      maxCount = 3,
      maxSelectable = null,
      modalPopover = false,
      asChild = false,
      _className,
      renderOption,
      renderTrigger,
      searchText = 'Search...',
      notFoundText = 'No results.',
      selectAllText = 'Select all',
      error = false,
      disabled,
      addItemConfig,
      hideSelectAll = false,
      hideTags = false,
      id,
      hideSearch = false,
      dropdownAlignment = 'start',
      hideClearAll = false,
      listClassName,
      triggerTooltip,
      ...props
    },
    ref
  ) => {
    const [selectedValues, setSelectedValues] =
      React.useState<string[]>(defaultValue);
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    const [searchQuery, setSearchQuery] = React.useState('');
    const [displayedOptions, setDisplayedOptions] =
      React.useState<Option[]>(options);

    const sortOptions = React.useCallback(
      (optionsToSort: Option[]) => {
        const selected = optionsToSort.filter((option) =>
          selectedValues.includes(option.value)
        );
        const unselected = optionsToSort.filter(
          (option) => !selectedValues.includes(option.value)
        );
        return [...selected, ...unselected];
      },
      [selectedValues]
    );

    React.useEffect(() => {
      // Only (re)sort while the menu is CLOSED, so selected items rise to the top for the
      // next open. While open, keep the order stable — re-sorting on an `options` change
      // (e.g. a data refetch) would yank a just-selected row to the top and reset cmdk's
      // active item (breaking keyboard navigation).
      if (!isPopoverOpen) {
        setDisplayedOptions(sortOptions(options));
      }
    }, [options, isPopoverOpen, sortOptions]);

    const filteredOptions = React.useMemo(() => {
      return displayedOptions?.filter((option) =>
        option.label?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }, [displayedOptions, searchQuery]);

    // Grouping is active when at least one option declares a group label.
    const isGrouped = React.useMemo(
      () => options.some((option) => option.group),
      [options]
    );

    // Ordered list of group keys based on first appearance in the original
    // `options` prop, so headings stay stable regardless of selection sorting.
    const groupOrder = React.useMemo(() => {
      const order: string[] = [];
      const seen = new Set<string>();
      options.forEach((option) => {
        const key = option.group ?? UNGROUPED_KEY;
        if (!seen.has(key)) {
          seen.add(key);
          order.push(key);
        }
      });
      return order;
    }, [options]);

    React.useEffect(() => {
      setSelectedValues(defaultValue);
    }, [defaultValue]);

    const handlePopoverOpenChange = (open: boolean) => {
      if (!open) {
        setSearchQuery('');
      } else {
        setDisplayedOptions(sortOptions(options));
      }
      setIsPopoverOpen(open);
    };

    const handleInputKeyDown = (
      event: React.KeyboardEvent<HTMLInputElement>
    ) => {
      if (event.key === 'Enter') {
        setIsPopoverOpen(true);
      } else if (event.key === 'Backspace' && !event.currentTarget.value) {
        const newSelectedValues = [...selectedValues];
        newSelectedValues.pop();
        setSelectedValues(newSelectedValues);
        onValueChange(newSelectedValues);
      }
    };

    const toggleOption = (value: string) => {
      const option = options.find((opt) => opt.value === value);
      if (option?.disabled) return;
      if (
        maxSelectable &&
        selectedValues.length >= maxSelectable &&
        !selectedValues.includes(value)
      ) {
        return;
      }
      const newSelectedValues = selectedValues.includes(value)
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value];
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    };

    const handleClear = () => {
      setSelectedValues([]);
      onValueChange([]);
    };

    const clearExtraOptions = () => {
      const newSelectedValues = selectedValues.slice(0, maxCount);
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    };

    const toggleAll = () => {
      if (selectedValues.length === options.length) {
        handleClear();
      } else {
        const allValues = options.map((option) => option.value);
        setSelectedValues(allValues);
        onValueChange(allValues);
      }
    };

    const renderOptionItem = (option: Option) => {
      const isSelected = selectedValues.includes(option.value);
      return (
        <CommandItem
          key={option.value}
          onSelect={() => toggleOption(option.value)}
          className={cn(
            'gap-lg rounded-none hover:bg-bg-primary-hover',
            option.disabled && 'cursor-not-allowed pointer-events-none'
          )}
          {...(option.disabled ? { tabIndex: -1 } : {})}
          data-disabled={option.disabled}
          value={option.value}
        >
          {renderOption ? (
            renderOption({
              option,
              isSelected,
              onSelect: toggleOption,
            })
          ) : (
            <div className="flex items-center gap-lg">
              {isSelected ? (
                <Checkbox size="sm" checked />
              ) : (
                <Checkbox size="sm" checked={false} />
              )}
              <span>{option.label}</span>
            </div>
          )}
        </CommandItem>
      );
    };

    return (
      <Popover
        open={isPopoverOpen}
        onOpenChange={handlePopoverOpenChange}
        modal={modalPopover}
      >
        {/* Optional trigger tooltip. Disabled (passthrough) when no triggerTooltip is set;
            asChild composes onto the PopoverTrigger so the click still opens the menu. */}
        <Tooltip
          content={triggerTooltip?.content ?? ''}
          {...triggerTooltip}
          asChild
          disabled={!triggerTooltip || triggerTooltip.disabled}
        >
          <PopoverTrigger asChild>
          {renderTrigger ? (
            renderTrigger({
              selectedOptions: options.filter((option) =>
                selectedValues.includes(option.value)
              ),
              placeholder,
            })
          ) : (
            <button
              type="button"
              ref={ref}
              id={id}
              onClick={() => setIsPopoverOpen(true)}
              className={cn(
                'flex w-full box-border items-center justify-between rounded-md px-xl bg-bg-primary text-text-primary txt-small-regular ring-0 transition-colors [&>span]:line-clamp-1',
                !hideTags && selectedValues.length > 0
                  ? 'min-h-10 py-1'
                  : 'h-10 py-2.5',
                'disabled:cursor-not-allowed disabled:bg-bg-disabled-subtle disabled:text-text-disabled disabled:pointer-events-none appearance-none focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--color-bg-brand-hover)] focus-visible:border-fg-brand',
                'border border-border-primary hover:border-border-primary-hover hover:bg-bg-primary disabled:border-border-disabled',
                // Open state hides the border so it doesn't double up with the ring.
                isPopoverOpen &&
                  'ring-4 ring-[var(--color-bg-brand-hover)] border-fg-brand',
                error
                  ? 'border-border-error hover:border-border-error focus-visible:border-border-error-hover focus-visible:ring-border-error-hover'
                  : '',
                _className
              )}
              disabled={disabled}
              {...props}
            >
              {!hideTags && selectedValues.length > 0 ? (
                <div className="flex justify-between align-middle items-stretch w-full">
                  <div className="flex flex-wrap gap-2 items-center">
                    {selectedValues.slice(0, maxCount).map((value) => {
                      const option = options.find((o) => o.value === value);
                      return (
                        <Badge
                          key={value}
                          closeIcon={{
                            isShown: !option?.disabled,
                            onClose: (e) => {
                              e.stopPropagation();
                              if (option?.value) toggleOption(option.value);
                            },
                          }}
                          className="txt-small-medium text-text-secondary"
                        >
                          {option?.label}
                        </Badge>
                      );
                    })}
                    {selectedValues.length > maxCount && (
                      <Badge
                        closeIcon={{
                          isShown: !hideClearAll,
                          onClose: (e) => {
                            e.stopPropagation();
                            clearExtraOptions();
                          },
                        }}
                        className="txt-small-medium text-text-secondary"
                      >
                        {`+ ${selectedValues.length - maxCount} more`}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between align-middle self-stretch">
                    {!hideClearAll && (
                      <XIcon
                        className="size-4 mx-2 cursor-pointer text-fg-quaternary hover:text-fg-quaternary-hover"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleClear();
                        }}
                      />
                    )}
                    {!hideClearAll && (
                      <Separator
                        orientation="vertical"
                        className="flex min-h-6 h-full"
                      />
                    )}
                    <ChevronDown className="ml-1 h-4 w-4 shrink-0 text-fg-quaternary hover:text-fg-quaternary-hover cursor-pointer" />
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between w-full mx-auto">
                  <span className="txt-small-regular text-text-placeholder">
                    {placeholder}
                  </span>
                  <ChevronDown className="ml-1 h-4 w-4 shrink-0 text-fg-quaternary hover:text-fg-quaternary-hover cursor-pointer" />
                </div>
              )}
            </button>
          )}
          </PopoverTrigger>
        </Tooltip>
        <PopoverContent
          className="w-full p-0 min-w-[300px]"
          align={dropdownAlignment}
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
          style={{
            width: 'var(--radix-popper-anchor-width)',
          }}
        >
          <Command shouldFilter={false}>
            {!hideSearch ? (
              <CommandInput
                placeholder={searchText}
                onKeyDown={handleInputKeyDown}
                value={searchQuery}
                onValueChange={setSearchQuery}
              />
            ) : null}
            <CommandList
              className={cn(
                'max-h-[400px] overflow-y-auto overflow-x-hidden',
                listClassName
              )}
            >
              <CommandEmpty>{notFoundText}</CommandEmpty>
              <CommandGroup>
                {addItemConfig?.shouldAllowAdd ? (
                  <CommandItem
                    onSelect={() => {
                      addItemConfig.onAddItem();
                      setIsPopoverOpen(false);
                    }}
                    className="mx-0 rounded-none justify-start"
                  >
                    <PlusIcon className="mr-2 size-4" />
                    {addItemConfig?.placeholder || 'Add new item'}
                  </CommandItem>
                ) : null}
                {!hideSelectAll && filteredOptions.length ? (
                  <CommandItem
                    key="all"
                    onSelect={toggleAll}
                    className="cursor-pointer mx-0 rounded-none flex items-center gap-lg justify-start py-2.5 px-3 txt-small-regular border-b"
                  >
                    {selectedValues.length === options.length ? (
                      <Checkbox size="sm" checked />
                    ) : (
                      <Checkbox size="sm" checked={false} />
                    )}
                    <span className="txt-small-regular text-text-secondary">
                      {selectAllText}
                    </span>
                  </CommandItem>
                ) : null}
                {!isGrouped && filteredOptions.map(renderOptionItem)}
              </CommandGroup>
              {isGrouped &&
                groupOrder.map((groupKey) => {
                  const groupOptions = filteredOptions.filter(
                    (option) => (option.group ?? UNGROUPED_KEY) === groupKey
                  );
                  if (!groupOptions.length) return null;
                  return (
                    <CommandGroup
                      key={groupKey}
                      // Force the group-heading style here so it wins deterministically over
                      // the base Command/CommandGroup heading rules (equal-specificity selectors
                      // whose order — and thus the rendered label — otherwise varies per app build).
                      className="[&_[cmdk-group-heading]]:!px-lg [&_[cmdk-group-heading]]:!py-sm [&_[cmdk-group-heading]]:!text-xs [&_[cmdk-group-heading]]:!font-medium [&_[cmdk-group-heading]]:!text-text-quaternary"
                      {...(groupKey !== UNGROUPED_KEY
                        ? { heading: groupKey }
                        : {})}
                    >
                      {groupOptions.map(renderOptionItem)}
                    </CommandGroup>
                  );
                })}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }
);

MultiSelect.displayName = 'MultiSelect';

export { MultiSelect };
